import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, ArrowUpRight } from "lucide-react";
import { Section, SectionHeading, Reveal } from "../lib/ui";
import { SITE, NAV, SERVICE_OPTIONS, logo } from "../config";
import { scrollTo } from "./Navbar";
import { goNav } from "../lib/nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ContactMethods() {
  const emails = (SITE.BUSINESS_EMAILS || []).map((e) => e.trim()).filter(Boolean);
  const phones = (SITE.BUSINESS_PHONES || []).map((p) => p.trim()).filter(Boolean);
  const address = SITE.BUSINESS_ADDRESS?.trim();

  return (
    <div className="space-y-4">
      {emails.map((email, i) => (
        <a key={email} href={`mailto:${email}`} className="group flex items-center gap-4 hairline rounded-xl bg-ink-surface/60 p-5 hover:border-gold/40 transition-colors duration-400" data-testid={`contact-email-${i}`}>
          <span className="h-11 w-11 rounded-full border border-gold/30 flex items-center justify-center text-gold shrink-0"><Mail size={18} /></span>
          <div className="min-w-0">
            <div className="overline text-silver/40 text-[0.6rem]">Business Email</div>
            <div className="font-body text-sm text-white truncate group-hover:text-gold transition-colors">{email}</div>
          </div>
        </a>
      ))}
      {phones.map((phone, i) => (
        <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="group flex items-center gap-4 hairline rounded-xl bg-ink-surface/60 p-5 hover:border-gold/40 transition-colors duration-400" data-testid={`contact-phone-${i}`}>
          <span className="h-11 w-11 rounded-full border border-gold/30 flex items-center justify-center text-gold shrink-0"><Phone size={18} /></span>
          <div className="min-w-0">
            <div className="overline text-silver/40 text-[0.6rem]">Business Telephone</div>
            <div className="font-body text-sm text-white group-hover:text-gold transition-colors">{phone}</div>
          </div>
        </a>
      ))}
      {address && (
        <div className="flex items-start gap-4 hairline rounded-xl bg-ink-surface/60 p-5" data-testid="contact-address">
          <span className="h-11 w-11 rounded-full border border-gold/30 flex items-center justify-center text-gold shrink-0"><MapPin size={18} /></span>
          <div>
            <div className="overline text-silver/40 text-[0.6rem]">Business Address</div>
            <div className="font-body text-sm text-silver/80 leading-relaxed">{address}</div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY = { name: "", organisation: "", email: "", telephone: "", service: "", message: "" };

export function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Please enter your name";
    if (!form.email.trim()) err.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Please enter a valid email";
    if (!form.service) err.service = "Please select a service";
    if (!form.message.trim()) err.message = "Please enter a message";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry submitted", { description: "Thank you — Arco Orbis2 will be in touch." });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Unable to submit", { description: "Please try again shortly." });
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-ink border border-white/10 rounded-lg px-4 py-3 font-body text-sm text-white placeholder:text-silver/30 focus:border-gold/50 focus:outline-none transition-colors duration-300";

  return (
    <Section id="contact" className="bg-ink">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionHeading overline="Business Contact" title={<>Let's discuss your <span className="gold-text">security requirements.</span></>} />
          <Reveal delay={0.1}><p className="mt-6 text-silver/70 font-body font-light leading-relaxed">
            Tell us what's on your mind — whether it's a specific worry, a building you're responsible for, a trip
            you're planning, or you're just not sure where to start. We'll read every message ourselves and come
            back to you personally.
          </p></Reveal>
          <div className="mt-10"><ContactMethods /></div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} noValidate className="hairline rounded-2xl bg-ink-surface/50 p-6 md:p-8" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" error={errors.name}>
                  <input className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" data-testid="form-name" />
                </Field>
                <Field label="Organisation">
                  <input className={inputCls} value={form.organisation} onChange={set("organisation")} placeholder="Company / organisation" data-testid="form-organisation" />
                </Field>
                <Field label="Email" error={errors.email}>
                  <input type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="form-email" />
                </Field>
                <Field label="Telephone">
                  <input className={inputCls} value={form.telephone} onChange={set("telephone")} placeholder="Optional" data-testid="form-telephone" />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Service Required" error={errors.service}>
                  <select className={`${inputCls} appearance-none`} value={form.service} onChange={set("service")} data-testid="form-service">
                    <option value="" disabled>Select a service…</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-ink">{s}</option>)}
                  </select>
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Message" error={errors.message}>
                  <textarea rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your requirements…" data-testid="form-message" />
                </Field>
              </div>
              <button type="submit" disabled={loading} className="gold-sweep mt-6 inline-flex items-center gap-2 rounded-full bg-gold text-black px-8 py-4 text-sm font-body font-medium tracking-wide hover:bg-gold-light transition-colors duration-300 disabled:opacity-60" data-testid="form-submit">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Submitting…</> : <>Submit Enquiry <Send size={16} /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="overline text-silver/50 text-[0.6rem] mb-2 inline-block">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-gold-light font-body">{error}</span>}
    </label>
  );
}

export function Footer() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const email = (SITE.BUSINESS_EMAILS || []).map((e) => e.trim()).filter(Boolean)[0];
  const phone = (SITE.BUSINESS_PHONES || []).map((p) => p.trim()).filter(Boolean)[0];

  const go = (item) => goNav({ navigate, pathname, item, scrollTo });

  const servicesLinks = [
    { label: "Bespoke Security", to: "/services/bespoke-security" },
    { label: "Risk Management", to: "/services/risk-management" },
    { label: "Technical Solutions", to: "/services/technical-solutions" },
    { label: "Travel Risk Management", to: "/services/travel-risk-management" },
    { label: "Wider Risk Management", to: "/services/wider-risk-management" },
  ];
  const companyLinks = [
    { label: "About Arco Orbis2", to: "/about" },
    { label: "Our Story", to: "/our-story" },
    { label: "Meet Us", to: "/meet-us" },
    { label: "Get in Contact", to: "/contact" },
    { label: "Privacy Policy", to: "/privacy" },
  ];

  return (
    <footer className="relative bg-ink-surface border-t border-white/10 px-6 md:px-10 lg:px-16 pt-20 pb-10" data-testid="footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Arco Orbis2" className="h-12 w-12 object-contain" />
            <span className="notranslate font-display font-medium tracking-[0.25em] text-sm" translate="no">ARCO <span className="gold-text">ORBIS2</span></span>
          </div>
          <p className="mt-6 text-silver/50 font-body font-light text-sm leading-relaxed max-w-sm">
            Specialist security consultancy, risk management and technical advisory solutions for people,
            organisations, buildings and facilities.
          </p>
        </div>

        <FooterCol title="Navigation" entries={NAV} onGo={go} />
        <FooterCol title="Services" entries={servicesLinks} onGo={go} />
        <FooterCol title="Company" entries={companyLinks} onGo={go} />
      </div>

      <div className="max-w-7xl mx-auto mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-silver/40 font-body text-xs">© 2026 Arco Orbis2. All rights reserved.</p>
        <div className="flex items-center gap-5 text-xs font-body">
          <button onClick={() => navigate("/privacy")} className="text-silver/50 hover:text-gold transition-colors" data-testid="footer-privacy">Privacy Policy</button>
          {email && <a href={`mailto:${email}`} className="text-silver/50 hover:text-gold transition-colors inline-flex items-center gap-1" data-testid="footer-email">Email <ArrowUpRight size={12} /></a>}
          {phone && <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="text-silver/50 hover:text-gold transition-colors" data-testid="footer-phone">Call</a>}
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, entries, onGo }) {
  return (
    <div>
      <h4 className="overline text-gold/70 mb-5">{title}</h4>
      <ul className="space-y-3">
        {entries.map((item) => (
          <li key={item.label}>
            <button
              onClick={() => onGo(item)}
              className="font-body text-sm text-silver/50 hover:text-white transition-colors duration-300 text-left"
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
